// Toys Hunter — ricerca con suggerimenti nell'header.
//
// Mentre si scrive chiede a Shopify la sezione th-predictive-search
// (/search/suggest), ne prende `.thh-sugg__inner` e la mostra nel pannello.
// Prima di scrivere il pannello mostra ricerche recenti, popolari e prodotti in
// evidenza, gia resi nel markup dell'header.

const RECENT_KEY = 'th:recent-searches';
const MAX_RECENT = 6;
const MIN_CHARS = 2;
const DEBOUNCE_MS = 180;
const PANEL_MAX_WIDTH = 920;
const PANEL_GUTTER = 16;
const PANEL_OFFSET = 8;
const PANEL_MIN_HEIGHT = 240;

// Campi su cui cerca Shopify: oltre al titolo, SKU, marca, tipo, tag e variante,
// cosi "GX-13", "Bandai" o "Myth Cloth" trovano il prodotto anche senza titolo esatto.
const SEARCH_FIELDS = 'title,product_type,variants.sku,variants.title,vendor,tag';
const RESOURCE_TYPES = 'product,collection,page,article,query';

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const readRecent = () => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list.filter((term) => typeof term === 'string') : [];
  } catch {
    return [];
  }
};

const writeRecent = (list) => {
  try {
    if (list.length) localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    else localStorage.removeItem(RECENT_KEY);
  } catch {
    // Storage bloccato (navigazione privata): le recenti semplicemente non si salvano.
  }
  document.querySelectorAll('th-search').forEach((box) => box.renderRecent?.());
};

class ThSearch extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-disabled')) return;

    this.input = this.querySelector('[data-th-search-input]');
    this.panel = this.querySelector('[data-th-search-panel]');
    this.results = this.querySelector('[data-th-search-results]');
    this.empty = this.querySelector('[data-th-search-empty]');
    this.form = this.querySelector('form');
    if (!this.input || !this.panel || !this.results || !this.empty || !this.form) return;

    this.active = -1;
    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.input.addEventListener('focus', () => this.showEmptyOrResults(), { signal });
    this.input.addEventListener('input', () => this.onInput(), { signal });
    this.input.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
    this.form.addEventListener('submit', () => this.remember(this.input.value), { signal });
    this.panel.addEventListener('click', (event) => this.onPanelClick(event), { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    }, { signal });

    // Il pannello si riposiziona quando cambia lo spazio: rotazione, finestra
    // ridimensionata, tastiera del telefono che riduce la parte visibile.
    const reposition = () => {
      if (!this.panel.hidden) this.position();
    };
    window.addEventListener('resize', reposition, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', reposition, { signal, passive: true });

    this.renderRecent();
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.request?.abort();
    clearTimeout(this.timer);
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.position();
  }

  // Tiene il pannello dentro lo schermo. Largo al massimo PANEL_MAX_WIDTH, si
  // sposta a sinistra quando partendo dal bordo della barra uscirebbe a destra
  // (tablet e portatili piccoli), e l'altezza massima parte da dove inizia il
  // pannello, non da una misura fissa: su telefono l'header e piu alto.
  position() {
    const rect = this.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const isMobile = this.classList.contains('thh-searchbox--mobile');

    const width = isMobile ? rect.width : Math.min(PANEL_MAX_WIDTH, viewportWidth - PANEL_GUTTER * 2);
    const overflowRight = rect.left + width - (viewportWidth - PANEL_GUTTER);
    const shift = Math.max(PANEL_GUTTER - rect.left, -Math.max(0, overflowRight));
    const top = rect.bottom + PANEL_OFFSET;

    this.panel.style.width = `${Math.round(width)}px`;
    this.panel.style.left = `${Math.round(isMobile ? 0 : shift)}px`;
    this.panel.style.maxHeight = `${Math.round(Math.max(PANEL_MIN_HEIGHT, viewportHeight - top - PANEL_GUTTER))}px`;
  }

  close() {
    if (this.panel.hidden) return;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    this.setActive(-1);
  }

  hasEmptyContent() {
    return Array.from(this.empty.children).some((group) => !group.hidden);
  }

  showEmptyOrResults() {
    if (this.input.value.trim().length >= MIN_CHARS && this.results.childElementCount) {
      this.open();
    } else {
      this.showEmpty();
    }
  }

  showEmpty() {
    this.request?.abort();
    this.results.replaceChildren();
    this.empty.hidden = false;
    this.classList.remove('is-loading');
    this.setActive(-1);
    if (this.hasEmptyContent()) this.open();
    else this.close();
  }

  onInput() {
    clearTimeout(this.timer);
    const term = this.input.value.trim();
    if (term.length < MIN_CHARS) {
      this.showEmpty();
      return;
    }
    this.timer = setTimeout(() => this.fetchResults(term), DEBOUNCE_MS);
  }

  buildUrl(term) {
    // data-url ha gia il prefisso della lingua: senza, i risultati arriverebbero
    // sempre nella lingua principale del negozio.
    const base = this.dataset.url || window.Theme?.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(base, window.location.origin);
    const params = url.searchParams;
    params.set('q', term);
    params.set('section_id', this.dataset.section);
    params.set('resources[type]', RESOURCE_TYPES);
    params.set('resources[limit]', this.dataset.limit || '6');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', SEARCH_FIELDS);
    return url;
  }

  async fetchResults(term) {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.classList.add('is-loading');

    try {
      const response = await fetch(this.buildUrl(term), { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const markup = await response.text();
      if (request.signal.aborted) return;

      const inner = new DOMParser().parseFromString(markup, 'text/html').querySelector('.thh-sugg__inner');
      if (!inner) throw new Error('Risultati senza markup atteso');

      this.highlight(inner, term);
      this.results.replaceChildren(inner);
      // Senza risultati restano visibili ricerche recenti e popolari, come via d'uscita.
      this.empty.hidden = !inner.querySelector('[data-th-search-noresults]');
      this.setActive(-1);
      this.open();
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Errore di rete o sezione mancante: si torna ai suggerimenti statici, e
      // l'invio del form porta comunque alla pagina di ricerca.
      this.showEmpty();
    } finally {
      if (this.request === request) this.classList.remove('is-loading');
    }
  }

  highlight(root, term) {
    const words = term.split(/\s+/).filter((word) => word.length >= MIN_CHARS).map(escapeRegExp);
    if (!words.length) return;
    const pattern = new RegExp(`(${words.join('|')})`, 'gi');

    root.querySelectorAll('[data-th-highlight]').forEach((node) => {
      const text = node.textContent;
      if (!pattern.test(text)) return;
      pattern.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let last = 0;
      text.replace(pattern, (match, _group, offset) => {
        fragment.append(text.slice(last, offset));
        const mark = document.createElement('mark');
        mark.textContent = match;
        fragment.append(mark);
        last = offset + match.length;
        return match;
      });
      fragment.append(text.slice(last));
      node.replaceChildren(fragment);
    });
  }

  items() {
    return Array.from(this.panel.querySelectorAll('[data-sugg-item]')).filter(
      (item) => !item.closest('[hidden]') && item.getClientRects().length > 0,
    );
  }

  setActive(index, items = this.items()) {
    items.forEach((item, position) => {
      const on = position === index;
      item.classList.toggle('is-active', on);
      if (on) {
        if (!item.id) item.id = `${this.panel.id}-opt-${position}`;
        item.setAttribute('aria-selected', 'true');
        item.scrollIntoView({ block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    });
    this.active = index;
    if (index >= 0 && items[index]) this.input.setAttribute('aria-activedescendant', items[index].id);
    else this.input.removeAttribute('aria-activedescendant');
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      if (!this.panel.hidden) {
        event.preventDefault();
        this.close();
      }
      return;
    }

    if (event.key === 'Enter') {
      const current = this.items()[this.active];
      if (!this.panel.hidden && current) {
        event.preventDefault();
        if (current.dataset.term) this.remember(current.dataset.term);
        current.click();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (this.panel.hidden) this.showEmptyOrResults();
    const items = this.items();
    if (!items.length) return;
    event.preventDefault();
    const count = items.length;
    const next = event.key === 'ArrowDown' ? (this.active + 1) % count : (this.active - 1 + count) % count;
    this.setActive(next, items);
  }

  onPanelClick(event) {
    if (event.target.closest('[data-th-search-clear]')) {
      event.preventDefault();
      writeRecent([]);
      this.input.focus();
      if (!this.hasEmptyContent()) this.close();
      return;
    }
    const link = event.target.closest('a[data-term]');
    if (link) this.remember(link.dataset.term);
  }

  remember(value) {
    const term = (value || '').trim();
    if (term.length < MIN_CHARS) return;
    const rest = readRecent().filter((saved) => saved.toLowerCase() !== term.toLowerCase());
    writeRecent([term, ...rest].slice(0, MAX_RECENT));
  }

  renderRecent() {
    const group = this.querySelector('[data-th-search-recent]');
    const list = this.querySelector('[data-th-search-recent-list]');
    if (!group || !list || !this.form) return;

    const terms = readRecent();
    group.hidden = terms.length === 0;
    list.replaceChildren(
      ...terms.map((term) => {
        const url = new URL(this.form.action, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');

        const link = document.createElement('a');
        link.className = 'thh-sugg__pill';
        link.href = `${url.pathname}${url.search}`;
        link.setAttribute('role', 'option');
        link.dataset.suggItem = '';
        link.dataset.term = term;
        link.textContent = term;

        const item = document.createElement('li');
        item.append(link);
        return item;
      }),
    );
  }
}

if (!customElements.get('th-search')) {
  customElements.define('th-search', ThSearch);
}
